import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tooltip } from 'antd';
import cn from 'classnames';
import { useMemo } from 'react';

import newClientIcon from '@app/assets/icons/new_icon_blue.svg';

import type { OverlapLayoutParams } from '@pages/Calendar/utils';

import type {
  AppointmentPreviewField,
  CalendarEvent,
  Employee,
} from '@pages/Calendar/data';

import { AppointmentMoveModal } from '../../AppointmentMoveModal';
import {
  useAppointmentCardModel,
  useAppointmentInteraction,
  type EventDragState,
} from '../hooks';

import { AppointmentCardContent } from './AppointmentCardContent';
import { AppointmentCardInfoTooltip } from './AppointmentCardInfoTooltip';

import '../styles/AppointmentCard.scss';

import type { CSSProperties, FC } from 'react';

interface AppointmentCardProps {
  event: CalendarEvent;
  employees: Employee[];
  layout: OverlapLayoutParams;
  /**
   * Whether this preview is the hover-resolved appointment for the current
   * pointer position. Elevation is controlled by the parent grid's hover
   * arbitration so overlapping previews stay individually reachable.
   */
  isActive?: boolean;
  calendarStartTime: string;
  calendarEndTime: string;
  stepMinutes: number;
  intervalHeightPx: number;
  onEventUpdate: (
    eventId: string,
    newStart: string,
    newEnd: string,
    newEmployeeId?: string
  ) => void;
  onDragStateChange?: (state: EventDragState | null) => void;
  onRecordClick?: (event: CalendarEvent) => void;
  moveEntityLabel?: string;
  interactionsDisabled?: boolean;
  canShowContacts: boolean;
  previewFields: AppointmentPreviewField[];
}

const APPOINTMENT_INTERACTION_Z_INDEX = 1000000;
const APPOINTMENT_CARD_HOVER_Z_INDEX = 900000;

export const AppointmentCard: FC<AppointmentCardProps> = ({
  event,
  employees,
  layout,
  isActive = false,
  calendarStartTime,
  calendarEndTime,
  stepMinutes,
  intervalHeightPx,
  onEventUpdate,
  onDragStateChange,
  onRecordClick,
  moveEntityLabel,
  interactionsDisabled = false,
  canShowContacts,
  previewFields,
}) => {
  const {
    interactionMode,
    pendingMove,
    previewTopPx,
    previewHeightPx,
    previewStart,
    previewEnd,
    previewEmployeeId,
    handleBodyPointerDown,
    handleResizeTopPointerDown,
    handleResizeBottomPointerDown,
    handlePointerMove,
    handlePointerUp,
    clearPendingMove,
  } = useAppointmentInteraction({
    event,
    layout,
    calendarStartTime,
    calendarEndTime,
    stepMinutes,
    intervalHeightPx,
    onEventUpdate,
    onDragStateChange,
    onRecordClick,
    canShowContacts,
  });

  const originalEmployee = employees.find((e) => e.id === event.employeeId);
  const targetEmployee = pendingMove
    ? employees.find((e) => e.id === pendingMove.newEmployeeId)
    : null;

  const isDragging = interactionMode === 'move';
  const isResizing =
    interactionMode === 'resize-top' || interactionMode === 'resize-bottom';

  // When dragging to another column the overlay renders the preview there.
  // Hide the original card so there are no two simultaneous previews.
  const isPreviewInAnotherColumn =
    isDragging && previewEmployeeId !== event.employeeId;

  const {
    bodyBackgroundColor,
    borderAccentColor,
    clientName,
    contentTextColor,
    headerBackgroundColor,
    hiddenServicesCount,
    hiddenTagsCount,
    isExtended,
    isFirstVisit,
    isLoyaltyCardVisible,
    phone,
    previewShadow,
    primaryText,
    secondaryText,
    showComment,
    showPhone,
    showServices,
    showTags,
    statusIcon,
    statusIconColor,
    statusIcons,
    stopCardInteraction,
    tagsOptions,
    visibleComment,
    visibleServices,
    visibleTags,
  } = useAppointmentCardModel({
    event,
    previewHeightPx,
    canShowContacts,
  });

  const shouldShowClientRow = Boolean(clientName);
  const shouldShowPhoneRow = Boolean(showPhone && phone);
  const shouldShowServicesRows = showServices && visibleServices.length > 0;
  const shouldShowTagsRow = showTags && visibleTags.length > 0;
  const shouldShowCommentRow = Boolean(showComment && visibleComment);

  const cardZIndex = useMemo(() => {
    if (isDragging || isResizing) {
      return APPOINTMENT_INTERACTION_Z_INDEX;
    }

    if (isActive) {
      return APPOINTMENT_CARD_HOVER_Z_INDEX;
    }

    return layout.zIndex;
  }, [isDragging, isActive, isResizing, layout.zIndex]);

  const cardStyle = useMemo<CSSProperties>(
    () => ({
      top: previewTopPx,
      height: previewHeightPx,
      left: layout.leftPx,
      right: layout.rightPx,
      zIndex: cardZIndex,
      opacity: isPreviewInAnotherColumn ? 0 : undefined,
      pointerEvents: isPreviewInAnotherColumn ? 'none' : undefined,
      boxShadow: previewShadow,
    }),
    [
      cardZIndex,
      isPreviewInAnotherColumn,
      layout.leftPx,
      layout.rightPx,
      previewHeightPx,
      previewShadow,
      previewTopPx,
    ]
  );

  return (
    <>
      <div
        className={cn('AppointmentCard', {
          'AppointmentCard--dragging': isDragging,
          'AppointmentCard--resizing': isResizing,
          'AppointmentCard--extended': isExtended,
          'AppointmentCard--readonly': interactionsDisabled,
        })}
        style={cardStyle}
        onPointerMove={interactionsDisabled ? undefined : handlePointerMove}
        onPointerUp={interactionsDisabled ? undefined : handlePointerUp}
        data-testid={`appointment-card-${event.id}`}
      >
        <div
          className="AppointmentCard__resize-handle AppointmentCard__resize-handle--top"
          onPointerDown={
            interactionsDisabled ? undefined : handleResizeTopPointerDown
          }
        />

        <div
          className="AppointmentCard__inner"
          style={{
            borderLeftColor: borderAccentColor,
          }}
          onClick={
            interactionsDisabled && onRecordClick
              ? () => onRecordClick(event)
              : undefined
          }
          onPointerDown={
            interactionsDisabled ? undefined : handleBodyPointerDown
          }
        >
          <div
            className="AppointmentCard__header"
            style={{
              backgroundColor: headerBackgroundColor,
            }}
          >
            <span className="AppointmentCard__time-range">
              {previewStart} – {previewEnd}
            </span>

            <div className="AppointmentCard__icons">
              {isFirstVisit && (
                <Tooltip title="Новый клиент">
                  <span className="AppointmentCard__status-icon">
                    <img
                      src={newClientIcon}
                      alt="Новый клиент"
                      className="AppointmentCard__new-icon"
                    />
                  </span>
                </Tooltip>
              )}

              {statusIcons.map(({ key, icon, title }) => (
                <Tooltip key={key} title={title}>
                  <span className="AppointmentCard__status-icon">
                    <FontAwesomeIcon
                      icon={icon}
                      className="AppointmentCard__icon"
                    />
                  </span>
                </Tooltip>
              ))}

              {/* Иконка статуса показывает статус записи и одновременно
                  открывает детали записи по наведению; при наличии карты
                  лояльности она же окрашена фиолетовым. Название статуса
                  дублируется бейджем внутри самих деталей. */}
              {statusIcon && (
                <Tooltip
                  color="white"
                  title={
                    <AppointmentCardInfoTooltip
                      record={event.rawRecord}
                      tagOptions={tagsOptions}
                      canShowContacts={canShowContacts}
                    />
                  }
                  placement="right"
                  styles={{ root: { maxWidth: 300 } }}
                >
                  <button
                    type="button"
                    className={cn('AppointmentCard__icon-button', {
                      'AppointmentCard__icon-button--loyalty':
                        isLoyaltyCardVisible,
                    })}
                    onPointerDown={stopCardInteraction}
                    onClick={stopCardInteraction}
                  >
                    <FontAwesomeIcon
                      icon={statusIcon.icon}
                      color={statusIconColor}
                      className="AppointmentCard__icon"
                    />
                  </button>
                </Tooltip>
              )}
            </div>
          </div>

          <div
            className="AppointmentCard__body"
            style={{
              backgroundColor: bodyBackgroundColor,
              color: contentTextColor,
            }}
          >
            <AppointmentCardContent
              previewFields={previewFields}
              services={shouldShowServicesRows ? visibleServices : []}
              hiddenServicesCount={hiddenServicesCount}
              primaryText={primaryText}
              secondaryText={secondaryText}
              phone={shouldShowPhoneRow ? phone : undefined}
              clientName={shouldShowClientRow ? clientName : undefined}
              tags={shouldShowTagsRow ? visibleTags : []}
              hiddenTagsCount={hiddenTagsCount}
              comment={shouldShowCommentRow ? visibleComment : undefined}
            />
          </div>
        </div>

        <div
          className="AppointmentCard__resize-handle AppointmentCard__resize-handle--bottom"
          onPointerDown={
            interactionsDisabled ? undefined : handleResizeBottomPointerDown
          }
        />
      </div>

      {pendingMove && (
        <AppointmentMoveModal
          isOpen
          originalStart={event.start}
          originalEnd={event.end}
          newStart={pendingMove.newStart}
          newEnd={pendingMove.newEnd}
          fromEmployeeName={originalEmployee?.name}
          toEmployeeName={targetEmployee?.name}
          entityLabel={moveEntityLabel}
          onConfirm={() => {
            onEventUpdate(
              event.id,
              pendingMove.newStart,
              pendingMove.newEnd,
              pendingMove.newEmployeeId
            );
            clearPendingMove();
          }}
          onCancel={clearPendingMove}
        />
      )}
    </>
  );
};
